import { Bookmark, ExternalLink, FileText, Search, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';

interface MobileLandingProps {
  onStartSearch: () => void;
}

const steps = [
  {
    icon: Search,
    title: 'Search official sources',
    text: 'Find material from the Federal Register, Congress.gov, and FRED.',
  },
  {
    icon: FileText,
    title: 'Open the context',
    text: 'Review the date, surrounding text, and canonical source link.',
  },
  {
    icon: Bookmark,
    title: 'Build a trail',
    text: 'Save receipts into research collections that sync to your account.',
  },
  {
    icon: Share2,
    title: 'Share the receipt',
    text: 'Download a clean PNG or share the original source link.',
  },
];

export function MobileLanding({ onStartSearch }: MobileLandingProps) {
  return (
    <div className="px-5 pb-32 pt-28">
      <section>
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.12em] text-[#E1062C]">
          Evidence, with the link
        </p>
        <h1 className="font-display text-5xl font-bold leading-[0.95] tracking-[-0.03em] text-[#0B0D10]">
          Turn claims into receipts.
        </h1>
        <p className="mt-6 text-base leading-relaxed text-[#6B6F78]">
          Search public government sources, review dated context, and save evidence you can verify.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            onClick={onStartSearch}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E1062C] px-6 py-4 font-medium text-white"
          >
            <Search className="h-4 w-4" />
            Start a search
          </button>
          <Link
            to="/signup"
            className="w-full rounded-full border border-[rgba(11,13,16,0.16)] bg-white px-6 py-4 text-center font-medium text-[#0B0D10]"
          >
            Create free account
          </Link>
        </div>
      </section>

      <section className="mt-14 rounded-2xl border border-[rgba(11,13,16,0.08)] bg-white p-5 shadow-sm">
        <p className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
          Connected sources
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {['Federal Register', 'Congress.gov', 'FRED'].map((source) => (
            <span
              key={source}
              className="rounded-full bg-[#F6F5F2] px-3 py-2 text-sm text-[#0B0D10]"
            >
              {source}
            </span>
          ))}
        </div>
      </section>

      <section className="mt-16">
        <h2 className="font-display text-3xl font-bold text-[#0B0D10]">
          From question to citation.
        </h2>
        <div className="mt-6 space-y-3">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <article
                key={step.title}
                className="rounded-2xl border border-[rgba(11,13,16,0.08)] bg-white p-5"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E1062C]">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-display mt-4 text-lg font-bold text-[#0B0D10]">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#6B6F78]">{step.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mt-16 rounded-3xl bg-[#0B0D10] p-6 text-white">
        <ExternalLink className="h-6 w-6 text-[#E1062C]" />
        <h2 className="font-display mt-5 text-3xl font-bold">Keep the source attached.</h2>
        <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">
          Every result includes a canonical link so you and your audience can inspect the original.
        </p>
        <Link
          to="/signin"
          className="mt-6 block rounded-full bg-white px-5 py-3 text-center font-medium text-[#0B0D10]"
        >
          Sign in
        </Link>
      </section>
    </div>
  );
}
